import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import { boardTypeOf, useCreateTrackerMutation } from '@/entities/tracker';
import type { TemplateCard } from '@/entities/tracker-template';
import { notificationService } from '@/lib/services/notification.service';
import { track } from '@/shared/analytics';
import { ROUTES } from '@/shared/config/routes';

export function useCreateTrackerFromTemplate({ workspaceId }: { workspaceId: string }) {
  const { t } = useTranslation('trackers');
  const navigate = useNavigate();
  const createTracker = useCreateTrackerMutation({ workspaceId });

  const pick = (card: TemplateCard, options: { onCreated?: () => void } = {}) => {
    const snapshot =
      card.source === 'builtin'
        ? {
            name: card.builtin.displayName,
            itemSource: card.builtin.itemSource,
            groupBy: card.builtin.groupBy ?? null,
            layout: card.builtin.layout,
            icon: card.builtin.icon,
            hue: card.builtin.hue,
          }
        : {
            name: card.template.name,
            itemSource: card.template.itemSource,
            groupBy: card.template.groupBy ?? null,
            layout: card.template.layout ?? null,
            icon: card.template.icon,
            hue: card.template.hue,
          };

    createTracker.mutate(snapshot, {
      onSuccess: (created) => {
        track('tracker_template_used', {
          tracker_id: created.id,
          board_type: boardTypeOf(created.itemSource),
          template_source: card.source,
        });
        options.onCreated?.();
        void navigate(ROUTES.TRACKER_DETAIL(created.id));
      },
      onError: () => notificationService.showError(t('trackers.couldNotCreateTheTracker')),
    });
  };

  return { pick, isPending: createTracker.isPending };
}
